import React, { CSSProperties, ReactNode } from "react";

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  children: ReactNode;
  color?: string;
  hoverColor?: string;
  style?: CSSProperties;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  as?: React.ElementType;
  to?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  label,
  children,
  color = "transparent",
  hoverColor = "#e0e0e0",
  onClick,
  style,
  className = "",
  ariaLabel,
  disabled = false,
  as: Component = "button",
  to,
  ...rest
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const baseStyle: CSSProperties = {
    width: "44px",
    height: "44px",
    borderRadius: "7px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background-color 0.2s ease, transform 0.1s ease",
    backgroundColor: disabled ? "#f5f5f5" : isHovered ? hoverColor : color,
    border: "none",
    outline: "none",
    padding: 0,
    opacity: disabled ? 0.6 : 1,
    ...style,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e as any);
  };

  const buttonProps = {
    style: baseStyle,
    onClick: handleClick,
    onMouseEnter: () => !disabled && setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onMouseDown: () => !disabled && setIsHovered(false),
    onMouseUp: () => !disabled && setIsHovered(true),
    className: `icon-button ${className}`,
    "aria-label": ariaLabel || label,
    disabled,
    title: label,
    ...(Component === "button" ? { type: "button" as const } : {}),
    ...rest,
  };

  return React.createElement(
    Component,
    to ? { ...buttonProps, to } : buttonProps,
    children
  );
};
